/**
 * Helper para manejar errores de upload del backend
 * Detecta errores de formato, tamaño y muestra el modal correspondiente
 */

import { useFileError } from '../contexts/FileErrorContext';

/**
 * Maneja errores de upload y muestra el modal correspondiente
 * @param error - Error del backend
 * @param showFileSizeError - Función del contexto FileErrorContext
 * @returns true si el error fue manejado, false si no
 */
export const handleUploadError = (error: any, showFileSizeError: (message: string, isFormatError?: boolean) => void): boolean => {
  // Si es un error de rate limiting (429), no manejarlo aquí (se maneja en rateLimitHandler)
  if (error?.status === 429 || error?.statusCode === 429) {
    return false;
  }

  const errorMessage = error?.message || error?.error || '';
  const status = error?.status || error?.statusCode;

  // Detectar errores de formato (400 con mensaje de formato)
  const isFormatError = 
    status === 400 && (
      errorMessage.includes('formato') ||
      errorMessage.includes('tipo de archivo') ||
      errorMessage.includes('no permitido') ||
      errorMessage.includes('debe estar en formato') ||
      errorMessage.includes('solo pueden ser') ||
      errorMessage.includes('Magic bytes') ||
      errorMessage.includes('contenido del archivo no coincide') ||
      errorMessage.includes('posible archivo malicioso')
    );

  // Detectar errores de tamaño (413 o mensaje de tamaño)
  const isSizeError = 
    status === 413 ||
    errorMessage.includes('demasiado grande') ||
    errorMessage.includes('excede el tamaño') ||
    errorMessage.includes('tamaño máximo') ||
    errorMessage.includes('file too large') ||
    errorMessage.includes('File too large');

  if (isFormatError) {
    // Mostrar modal de error de formato
    showFileSizeError(errorMessage || 'El formato del archivo no es válido.', true);
    return true;
  }

  if (isSizeError) {
    // Mostrar modal de error de tamaño
    showFileSizeError(errorMessage || 'El archivo excede el tamaño máximo permitido.', false);
    return true;
  }

  // Si es un error 400 pero no es de formato, podría ser otro tipo de error de validación
  if (status === 400 && errorMessage) {
    // Si el mensaje sugiere un problema con el archivo, tratarlo como error de formato
    if (errorMessage.includes('archivo') || errorMessage.includes('file')) {
      showFileSizeError(errorMessage, true);
      return true;
    }
  }

  return false; // Error no manejado aquí
};
